import { useState } from "react";
import {
  Mic,
  Video,
  Globe,
  ArrowRight,
  BookOpen,
  Gamepad2,
  MonitorPlay,
  Package,
  Search,
  ShoppingBag,
  CalendarDays,
  ChartNoAxesCombined,
} from "zudoku/icons";
import { products, sections } from "./products.js";
import { SiteFooter } from "./SiteFooter.js";
import { SiteHeader } from "./SiteHeader.js";
import "./portal.css";

const icons = {
  live: MonitorPlay,
  voice: Mic,
  "one-to-one": Video,
  overseas: Globe,
  "game-companion": Gamepad2,
  rental: Package,
  commerce: ShoppingBag,
  quant: ChartNoAxesCombined,
  booking: CalendarDays,
};
export const Portal = () => {
  const [category, setCategory] = useState("全部产品");
  const [query, setQuery] = useState("");
  const filtered = products.filter(
    (product) =>
      (category === "全部产品" || product.category === category) &&
      [product.name, product.description, ...product.tags]
        .join(" ")
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
  );
  return (
    <div className="ax-portal">
      <title>安象产品中心</title>
      <meta
        name="description"
        content="查阅安象各产品的系统演示、功能介绍与技术文档。"
      />
      <SiteHeader />
      <main>
        <section className="ax-hero">
          <img
            className="ax-hero-image"
            src="/product/banner.png"
            alt="安象官网技术服务展示"
          />
          <div className="ax-container ax-hero-content">
            <p>安象科技 · 企业软件产品与服务</p>
            <h1>
              多场景产品体系
              <br />
              让业务落地更简单
            </h1>
            <p className="ax-hero-description">
              从直播社交到商业经营
              <br />
              一站了解产品、体验系统、查阅技术文档
            </p>
            <div className="ax-hero-actions">
              <a href="#products">
                探索产品 <ArrowRight size={18} />
              </a>
              <a href="/overview">进入文档中心</a>
            </div>
          </div>
        </section>
        <div className="ax-entry-bar">
          <div className="ax-container">
            <a href="#products">
              <Package size={26} />
              <div>
                <strong>产品与服务</strong>
                <span>按业务场景发现合适产品</span>
              </div>
              <ArrowRight size={16} />
            </a>
            <a href="/overview">
              <BookOpen size={26} />
              <div>
                <strong>产品文档</strong>
                <span>功能说明与技术框架</span>
              </div>
              <ArrowRight size={16} />
            </a>
            <a href="/support">
              <MonitorPlay size={26} />
              <div>
                <strong>系统体验</strong>
                <span>获取演示及产品咨询</span>
              </div>
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
        <section id="products" className="ax-container ax-products">
          <div className="ax-section-title">
            <div>
              <h2>产品与服务</h2>
              <p>按业务场景查找产品</p>
            </div>
            <form
              className="ax-catalog-search"
              role="search"
              onSubmit={(event) => event.preventDefault()}
            >
              <Search size={17} />
              <input
                aria-label="搜索产品"
                placeholder="搜索产品或功能"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {query && (
                <button type="button" onClick={() => setQuery("")}>
                  清除
                </button>
              )}
            </form>
          </div>
          <div className="ax-catalog-layout">
            <aside className="ax-catalog-sidebar">
              <p className="ax-catalog-label">产品分类</p>
              <div className="ax-filters" role="group" aria-label="产品分类">
                {["全部产品", "社交娱乐", "商业经营", "行业应用"].map(
                  (item) => (
                    <button
                      type="button"
                      aria-pressed={category === item}
                      className={category === item ? "selected" : ""}
                      key={item}
                      onClick={() => setCategory(item)}
                    >
                      <span>{item}</span>
                      <span className="ax-category-count">
                        {item === "全部产品"
                          ? products.length
                          : products.filter(
                              (product) => product.category === item,
                            ).length}
                      </span>
                    </button>
                  ),
                )}
              </div>
              <a className="ax-catalog-guide" href="/overview">
                <BookOpen size={18} /> 查看产品文档 <ArrowRight size={14} />
              </a>
            </aside>
            <div className="ax-catalog-results">
              <p className="ax-sr-only" role="status">
                找到 {filtered.length} 款产品
              </p>
              <div className="ax-grid">
                {filtered.map((product) => {
                  const Icon = icons[product.id];
                  return (
                    <article className="ax-product" key={product.id}>
                      <div className="ax-product-content">
                        <div className="ax-product-heading">
                          <div className="ax-product-symbol">
                            <Icon size={30} strokeWidth={1.5} />
                          </div>
                          <div>
                            <span className="ax-product-category">
                              {product.category}
                            </span>
                            <h3>
                              <a href={`/${product.id}/features`}>
                                {product.name}
                              </a>
                            </h3>
                          </div>
                          {(product.status || !product.ready) && (
                            <span className="ax-status">
                              {product.status ?? "资料整理中"}
                            </span>
                          )}
                        </div>
                        <p>{product.description}</p>
                        <div className="ax-tags">
                          {product.tags.map((tag) => (
                            <span key={tag}>{tag}</span>
                          ))}
                        </div>
                      </div>
                      <div className="ax-product-links">
                        {sections.map((section) => (
                          <a
                            key={section.slug}
                            href={`/${product.id}/${section.slug}`}
                          >
                            {section.label}
                            <ArrowRight size={14} />
                          </a>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>
              {filtered.length === 0 && (
                <div className="ax-empty">
                  <h3>没有找到相关产品</h3>
                  <p>试试其他关键词，或查看全部产品。</p>
                  <button
                    onClick={() => {
                      setCategory("全部产品");
                      setQuery("");
                    }}
                    type="button"
                  >
                    查看全部产品
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};
